import clsx from 'clsx';
import { getMonth } from 'date-fns';
import { ReactElement, useState } from 'react';
import Calendar, { CalendarProps } from 'react-calendar';

import { Icon } from '../icon/Icon';
import { Tooltip, TooltipTrigger } from '../tooltip/Tooltip';
import styles from './DatePicker.module.scss';

const CUSTOM_DISABLED_CLASS_NAME = 'react-calendar__tile--disabled';
const MONTH_ACTIVE_TILE = 'react-calendar__tile--hasActive';

interface NavigationTooltipFactoryProp {
  label: string;
  children: ReactElement | string;
}

export type DateValue = Date | null;

enum CalendarView {
  Month = 'month',
  Year = 'year',
  Decade = 'decade',
  Century = 'century',
}

export type DatePickerValue = DateValue | [DateValue, DateValue];

/** Translated strings used in the calendar's navigation tooltips. */
export interface DatePickerLocales {
  /** Tooltip on the year header in month-view (clicking it switches to year-view). */
  selectYear: string;
  /** Tooltip on the month header in month-view. */
  selectMonth: string;
  /** Tooltip on the decade header in year-view. */
  selectDecade: string;
  /** Tooltip on the previous-month chevron. */
  previousMonth: string;
  /** Tooltip on the previous-year double-chevron. */
  previousYear: string;
  /** Tooltip on the previous-decade chevron in year-view. */
  previousDecade: string;
  /** Tooltip on the next-month chevron. */
  nextMonth: string;
  /** Tooltip on the next-year double-chevron. */
  nextYear: string;
  /** Tooltip on the next-decade chevron in year-view. */
  nextDecade: string;
}

export interface DatePickerProp {
  /** Currently-selected date or `[start, end]` range. */
  value: DatePickerValue;
  /** When true, the user picks a `[start, end]` range instead of a single date. Defaults to `false`. */
  selectRange?: boolean;
  /** Latest selectable date. Tiles after this date are disabled. */
  maxDate?: Date;
  /** Earliest selectable date. Tiles before this date are disabled. */
  minDate?: Date;
  /** Called with the new value (single date or `[start, end]`) when the user makes a selection. */
  onDateChange: (newDate: DatePickerValue) => void;
  /** Translated strings for navigation tooltips (required for accessibility). */
  locales: DatePickerLocales;
  /** Custom predicate to disable individual tiles. Receives the tile's date and the current view. */
  tileDisabled?: (props: { date: Date; view: string }) => boolean;
}

/**
 * Calendar UI for picking a single date or a date range.
 *
 * Wraps `react-calendar` with the design-system styling, navigation
 * tooltips, and i18n labels. Locale comes from `navigator.language`.
 * For range mode set `selectRange={true}`; the value type changes to
 * `[start, end]` accordingly.
 */
export const DatePicker = ({
  value,
  selectRange = false,
  maxDate,
  minDate,
  onDateChange,
  locales,
  tileDisabled,
  ...rest
}: DatePickerProp) => {
  const [currentView, setCurrentView] = useState<CalendarProps['view']>(
    CalendarView.Month
  );

  const isInMonthView = currentView === CalendarView.Month;
  const isDecadeView = currentView === CalendarView.Decade;

  const navigationTooltipFactory = ({
    label,
    children,
  }: NavigationTooltipFactoryProp) => {
    return (
      <Tooltip
        isTouchDevice={false}
        label={label}
        strategy="fixed"
        trigger={TooltipTrigger.Hover}
        inPortal
      >
        <div>{children}</div>
      </Tooltip>
    );
  };

  const getSelectLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.selectYear;
      case CalendarView.Month:
        return locales.selectMonth;
      case CalendarView.Decade:
        return locales.selectDecade;
      default:
        return '';
    }
  };

  const getNextLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.nextYear;
      case CalendarView.Month:
        return locales.nextMonth;
      case CalendarView.Decade:
        return locales.nextDecade;
      default:
        return '';
    }
  };

  const getPreviousLabel = () => {
    switch (currentView) {
      case CalendarView.Year:
        return locales.previousYear;
      case CalendarView.Month:
        return locales.previousMonth;
      case CalendarView.Decade:
        return locales.previousDecade;
      default:
        return '';
    }
  };

  return (
    <div className={styles.datePickerWrapper} {...rest}>
      <Calendar
        className={clsx(styles.customDatePicker, {
          [styles.showNavLabel]:
            currentView === CalendarView.Month ||
            currentView === CalendarView.Year,
          [styles.selectRange]: isInMonthView && selectRange,
        })}
        value={value}
        selectRange={isInMonthView && selectRange}
        maxDate={maxDate}
        minDate={minDate}
        showNeighboringDecade
        minDetail={CalendarView.Decade}
        locale={navigator.language}
        formatDay={(locale, date) => {
          const day = date.getDate();
          return new Intl.NumberFormat(locale).format(day);
        }}
        tileDisabled={tileDisabled}
        tileClassName={({ date, view }) => {
          const classNames = [];
          const maxDateCondition =
            maxDate && new Date(date).getTime() > maxDate.getTime();
          const minDateCondition =
            minDate && new Date(date).getTime() < minDate.getTime();
          const customDisabled = tileDisabled && tileDisabled({ date, view });
          const shouldDisable =
            maxDateCondition || minDateCondition || customDisabled;

          if (shouldDisable) {
            classNames.push(CUSTOM_DISABLED_CLASS_NAME);
          }

          if (view === CalendarView.Year && value) {
            const monthOnCalendar = getMonth(date);
            const selectedMonth = getMonth(
              Array.isArray(value) ? (value[0] as Date) : value
            );

            if (monthOnCalendar === selectedMonth) {
              classNames.push(MONTH_ACTIVE_TILE);
            }
          }

          return classNames;
        }}
        formatMonth={(locale, date) => {
          return new Intl.DateTimeFormat(locale, { month: 'short' }).format(
            date
          );
        }}
        navigationLabel={({ label, view }) => {
          return view === CalendarView.Decade
            ? undefined
            : navigationTooltipFactory({
                label: getSelectLabel(),
                children: label,
              });
        }}
        nextLabel={
          !isDecadeView
            ? navigationTooltipFactory({
                label: getNextLabel(),
                children: <Icon iconName="ChevronLightRight" size={12} />,
              })
            : null
        }
        next2Label={
          isInMonthView
            ? navigationTooltipFactory({
                label: locales.nextYear,
                children: <Icon iconName="AngleLightRight" size={12} />,
              })
            : null
        }
        prevLabel={
          !isDecadeView
            ? navigationTooltipFactory({
                label: getPreviousLabel(),
                children: <Icon iconName="ChevronLightLeft" size={12} />,
              })
            : null
        }
        prev2Label={
          isInMonthView
            ? navigationTooltipFactory({
                label: locales.previousYear,
                children: <Icon iconName="AngleLightLeft" size={12} />,
              })
            : null
        }
        onChange={onDateChange}
        onViewChange={({ view }) => {
          setCurrentView(view);
        }}
      />
    </div>
  );
};
